import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchTerms } from "@/lib/logicApi";
import { useLogicUi } from "./logicUi";

/* Contextual terminology: known Stoic terms inside lesson text become
   clickable without leaving the page. Popover shows plain meaning first,
   with academic depth on request. */

export function useLogicTerms() {
  const [terms, setTerms] = useState([]);
  useEffect(() => { fetchTerms().then(setTerms).catch(() => {}); }, []);
  return terms;
}

function TermPopover({ term, onClose }) {
  const [deep, setDeep] = useState(false);
  const { ui, L } = useLogicUi();
  return (
    <span
      data-testid={`term-popover-${term.id}`}
      className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 z-50 block w-[340px] max-w-[88vw] border border-[#c8a95a]/40 bg-[#10141d] shadow-2xl p-5 text-left normal-case tracking-normal"
    >
      <span className="flex items-baseline justify-between gap-3">
        <span className="font-serif text-lg text-gold">{term.transliteration}</span>
        <span className="font-serif text-base text-bone/50">{term.original}</span>
      </span>
      <span className="block mt-2 text-[13px] text-fog font-reading leading-relaxed">{L(term.plain)}</span>
      {deep && (
        <span className="block mt-3 pt-3 border-t border-white/10 space-y-2">
          <span className="block text-[12px] text-bone/80 font-reading leading-relaxed"><span className="text-gold/70">{ui.academic} </span>{L(term.academic)}</span>
          <span className="block text-[12px] text-fog/80 font-reading leading-relaxed"><span className="text-gold/70">{ui.inCourse} </span>{L(term.application)}</span>
          <span className="block text-[12px] text-fog/70 font-reading italic leading-relaxed">{L(term.example)}</span>
        </span>
      )}
      <span className="flex items-center justify-between mt-3 pt-2 border-t border-white/10">
        <button type="button" data-testid={`term-popover-deepen-${term.id}`} onClick={(e) => { e.stopPropagation(); setDeep(!deep); }}
          className="text-[10px] tracking-[0.2em] uppercase text-gold/80 hover:text-gold">{deep ? ui.less : ui.goDeeper}</button>
        <Link to="/academy/logic/terms" className="text-[10px] tracking-[0.2em] uppercase text-[#8fa3c8] hover:text-gold">{ui.allTerms}</Link>
        <button type="button" onClick={(e) => { e.stopPropagation(); onClose(); }} aria-label="Close"
          className="text-[10px] tracking-[0.2em] uppercase text-fog/50 hover:text-fog">{ui.close}</button>
      </span>
    </span>
  );
}

function TermChip({ term, children }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative inline-block">
      <button type="button" data-testid={`term-chip-${term.id}`} onClick={() => setOpen(!open)}
        className="border-b border-dotted border-gold/60 text-inherit hover:text-gold focus:outline-none focus:text-gold cursor-help">
        {children}
      </button>
      {open && <TermPopover term={term} onClose={() => setOpen(false)} />}
    </span>
  );
}

export function TermText({ text, terms }) {
  if (!text || !terms?.length) return <>{text}</>;
  const patterns = [];
  terms.forEach((t) => (t.match || []).forEach((m) => patterns.push({ m, t })));
  patterns.sort((a, b) => b.m.length - a.m.length);
  const escaped = patterns.map((p) => p.m.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const re = new RegExp(`(${escaped.join("|")})`, "gi");
  const parts = String(text).split(re);
  if (parts.length === 1) return <>{text}</>;
  const lookup = (word) => patterns.find((p) => p.m.toLowerCase() === word.toLowerCase())?.t;
  return (
    <>
      {parts.map((part, i) => {
        const term = part && lookup(part);
        return term ? <TermChip key={i} term={term}>{part}</TermChip> : <span key={i}>{part}</span>;
      })}
    </>
  );
}
